import { useCallback, useMemo, useState } from "react";
import { useRegisterTopBarActions } from "../context/TopBarActionsContext";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Download, Printer } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import SuggestionFilters from "../components/SuggestionFilters";
import { statusColor } from "../components/StatusChip";
import { getSuggestionStats, listSuggestions } from "../api/ess";
import { displayDepartment } from "../api/departments";
import { useDepartments } from "../hooks/useDepartments";
import { unitGroupLabel } from "../utils/departments";
import {
  statusCategory,
  statusLabel,
  useWorkflowConfig,
} from "../hooks/useWorkflow";
import { downloadXlsx } from "../utils/xlsx";
import { inferPeriodLabel } from "../utils/period";
import { slaState } from "../utils/sla";
import {
  REWARD_CATEGORIES,
  resolveSuggestionUnit,
  suggestionEvaluations,
  type SuggestionListFilters,
  type SuggestionStatus,
} from "../types/ess";

import {
  REPORT_BUCKETS,
  buildOverviewPrintUrl,
  buildOverviewReport,
  buildSuggestionsUrl,
  buildTrend,
  priorPeriod,
  singleDepartment,
  type SuggestionsUrlParts,
} from "./reports/utils";
import HeroKPI from "./reports/HeroKPI";
import MetaTile from "./reports/MetaTile";
import TrendChart, { TREND_GRANULARITY_LABEL } from "./reports/TrendChart";
import Pipeline, {
  type PipelineStage,
  type RejectionBucket,
} from "./reports/Pipeline";
import BreakdownCard from "./reports/BreakdownCard";
import AnnualSscReport from "./reports/AnnualSscReport";

// ─── Main component ───────────────────────────────────────────────────

export default function Reports() {
  const [filters, setFilters] = useState<SuggestionListFilters>({});
  const [exporting, setExporting] = useState(false);
  const theme = useTheme();
  const { data: workflow } = useWorkflowConfig();

  const statsDepartment = singleDepartment(filters);

  const stats = useQuery({
    queryKey: [
      "ess-stats-reports",
      filters.date_from,
      filters.date_to,
      statsDepartment,
    ],
    queryFn: () =>
      getSuggestionStats({
        date_from: filters.date_from,
        date_to: filters.date_to,
        department: statsDepartment,
      }),
  });

  const prior = priorPeriod(filters.date_from, filters.date_to);
  const priorStats = useQuery({
    queryKey: [
      "ess-stats-reports-prior",
      prior?.date_from,
      prior?.date_to,
      statsDepartment,
    ],
    queryFn: () =>
      getSuggestionStats({
        date_from: prior!.date_from,
        date_to: prior!.date_to,
        department: statsDepartment,
      }),
    enabled: !!prior,
  });

  // Department -> unit-group lookup for the Unit filter's client-side
  // post-filter inside listSuggestions (same construction as Landing's —
  // `unitGroupLabel` maps common/unit-less departments to "Common").
  const { data: departmentList } = useDepartments();
  const unitByDepartment = useMemo(() => {
    const m: Record<string, string> = {};
    (departmentList ?? []).forEach((d) => {
      if (d?.name) m[d.name] = unitGroupLabel(d);
    });
    return m;
  }, [departmentList]);

  const list = useQuery({
    // unitByDepartment participates in the key so a late-arriving
    // department catalog re-runs the Unit post-filter.
    queryKey: ["ess-list-reports", filters, unitByDepartment],
    queryFn: () => listSuggestions(filters, unitByDepartment),
  });

  // Department dropdown options come from the un-filtered stats endpoint
  // (so the user can pick any department, not just ones present in the
  // currently filtered view).
  const departments = useMemo(
    () => Object.keys(stats.data?.by_department ?? {}).sort(),
    [stats.data],
  );

  const allRows = useMemo(
    () => list.data?.suggestions ?? [],
    [list.data],
  );

  // Legacy suggestions may carry a category from before the current
  // CATEGORIES list — source options from the un-filtered stats endpoint
  // (same as departments above) so the Category filter can still select
  // them even when the active Status/Department/Period filters would
  // otherwise hide every row in that category.
  const categories = useMemo(
    () => Object.keys(stats.data?.by_category ?? {}).sort(),
    [stats.data],
  );

  // Every figure on this page — and in the XLSX and the print route —
  // comes from this one model over the *filtered* list (reports/utils.ts).
  const report = useMemo(
    () => buildOverviewReport(allRows, workflow, unitByDepartment),
    [allRows, workflow, unitByDepartment],
  );
  const {
    total,
    pendingReview,
    closed,
    rejected,
    closureRate,
    rejectRate,
    medianCloseDays,
    agingCount,
    stateSets,
    topDepartment,
    committee,
  } = report;

  const priorClosureRate = useMemo(() => {
    if (!priorStats.data) return null;
    const pTotal = priorStats.data.total ?? 0;
    if (pTotal === 0) return null;
    const byStatusMap = priorStats.data.by_status ?? {};
    const pClosed = stateSets.closedStates.reduce(
      (sum, s) => sum + (byStatusMap[s] ?? 0),
      0,
    );
    return Math.round((pClosed * 100) / pTotal);
  }, [priorStats.data, stateSets.closedStates]);

  const trend = useMemo(
    () => buildTrend(allRows, filters.date_from, filters.date_to),
    [allRows, filters.date_from, filters.date_to],
  );

  // Base parts every drill-down URL carries: the active period plus the
  // unit / reward-band facets. Every count on this page is computed from
  // the *filtered* list, so a drill-down must preserve these filters or
  // the landing list won't match the number the user just clicked.
  // (Tile-specific extras spread after, so a tile's own department/
  // category/status still wins over the base.)
  const { date_from, date_to, unit, reward_category } = filters;
  const urlFor = useCallback(
    (extra: SuggestionsUrlParts) =>
      buildSuggestionsUrl({
        date_from,
        date_to,
        unit,
        reward_category,
        ...extra,
      }),
    [date_from, date_to, unit, reward_category],
  );

  // Drill-down forwards each stage's full reachable set, not just
  // happy-path descendants — keeps the resulting Suggestions list aligned
  // with the count the tile just showed.
  const pipelineStages: PipelineStage[] = useMemo(
    () =>
      report.pipeline.map((stage) => ({
        label: stage.label,
        count: stage.count,
        to: urlFor({
          label: stage.label,
          status: stage.reach as SuggestionStatus[],
          reached: stage.label,
        }),
        representativeStatus: stage.state,
        terminal: stage.terminal,
      })),
    [report.pipeline, urlFor],
  );

  const rejectionBuckets: RejectionBucket[] = useMemo(
    () =>
      report.rejections.map((r) => ({
        ...r,
        to: urlFor({ label: r.label, status: [r.status] }),
      })),
    [report.rejections, urlFor],
  );

  const exportXlsx = useCallback(async () => {
    setExporting(true);
    try {
      const summary: { Metric: string; Value: number | string }[] = [
        { Metric: "Total received", Value: total },
        { Metric: "Pending review", Value: pendingReview },
      ];
      // One row per happy-path stage: same labels and counts the user
      // sees in the on-screen pipeline.
      for (const stage of report.pipeline) {
        summary.push({ Metric: `Pipeline · ${stage.label}`, Value: stage.count });
      }
      summary.push(
        { Metric: "Rejected", Value: rejected },
        { Metric: "Closed", Value: closed },
        { Metric: "Closure rate (%)", Value: closureRate },
        { Metric: "Reject rate (%)", Value: rejectRate },
        { Metric: "Median days to close", Value: medianCloseDays ?? "n/a" },
        { Metric: "Aging (>14d in non-terminal)", Value: agingCount },
      );
      const byStatus = report.byStatus.map(({ label, count }) => ({
        Status: label,
        Count: count,
      }));
      const byCategory = report.byCategory.map(([Category, Count]) => ({
        Category,
        Count,
      }));
      const byDepartment = report.byDepartment.map(([Department, Count]) => ({
        Department,
        Count,
      }));
      // SC Summary — the Committee Report's buckets and boards, in the
      // same words as on screen and in print.
      const scSummarySheet: { Metric: string; Value: number | string }[] =
        REPORT_BUCKETS.map((b) => ({
          Metric: b.label,
          Value: committee.buckets[b.key],
        }));
      scSummarySheet.push({ Metric: "SLA breaches", Value: committee.slaBreaches });
      const [best] = committee.scEvaluations;
      if (best) {
        scSummarySheet.push({
          Metric: "Best suggestion (SC rating)",
          Value: `${best.suggestionNumber || "—"} · ${best.title} (${best.rating}%)`,
        });
      }
      for (const p of committee.mostApprovedPerPerson) {
        scSummarySheet.push({
          Metric: `Most approved per person · ${p.name} (${p.employeeId})`,
          Value: p.approved,
        });
      }
      // The "highest number of suggestions received" answer, then the
      // ranking it came from. The full, uncapped split stays on
      // "By department".
      if (topDepartment) {
        scSummarySheet.push({
          Metric: "Department with most suggestions",
          Value: `${displayDepartment(topDepartment[0])} (${topDepartment[1]})`,
        });
        for (const d of committee.topDepartments) {
          scSummarySheet.push({
            Metric: `Top department · ${displayDepartment(d.department)}`,
            Value: d.count,
          });
        }
      }
      const scEvaluationsSheet = committee.scEvaluations.map((r, idx) => ({
        "S/N": idx + 1,
        Originator: r.originator,
        "Employee ID": r.employeeId,
        "Suggestion No": r.suggestionNumber,
        Department: r.department,
        "SC rating %": r.rating,
        "Reward category": r.rewardCategory,
        Remark: r.remark,
      }));
      const rewardCategoriesSheet = REWARD_CATEGORIES.map((cat) => ({
        "Reward Category": cat,
        Count: committee.byRewardCategory[cat] ?? 0,
      }));

      const categoryOf = (status: string) => statusCategory(workflow, status);
      const detail = allRows.map((s) => ({
        "No.": s.suggestion_number ?? "",
        Title: s.title,
        Employee: s.employee_name,
        "Employee ID": s.employee_id,
        Department: s.department,
        Unit: resolveSuggestionUnit(s, unitByDepartment) ?? "",
        Category: s.category,
        "Reward Category": suggestionEvaluations(s).ssc?.reward_category ?? "",
        Status: statusLabel(workflow, s.status),
        "On behalf of": s.submitted_on_behalf_of ?? "",
        Submitted: s.created_at,
        "Last updated": s.updated_at,
        "SLA Breached": slaState(s, categoryOf).breached ? "Yes" : "No",
        Description: s.description,
        Remarks: s.remarks ?? "",
      }));

      const stamp = new Date().toISOString().slice(0, 10);
      await downloadXlsx(`ess-report-${stamp}.xlsx`, [
        { name: "Summary", rows: summary },
        { name: "By status", rows: byStatus },
        { name: "By category", rows: byCategory },
        { name: "By department", rows: byDepartment },
        { name: "SC Summary", rows: scSummarySheet },
        {
          name: "SC Evaluations",
          rows: scEvaluationsSheet,
          headers: [
            "S/N",
            "Originator",
            "Employee ID",
            "Suggestion No",
            "Department",
            "SC rating %",
            "Reward category",
            "Remark",
          ],
        },
        { name: "Reward Categories", rows: rewardCategoriesSheet },
        { name: "Suggestions", rows: detail },
      ]);
    } finally {
      setExporting(false);
    }
  }, [
    total,
    pendingReview,
    rejected,
    closed,
    closureRate,
    rejectRate,
    medianCloseDays,
    agingCount,
    report,
    committee,
    topDepartment,
    allRows,
    workflow,
    unitByDepartment,
  ]);

  const topBarActions = useMemo(
    () => (
      <Stack direction="row" spacing={1} data-tour="overview-actions">
        <Button
          variant="text"
          startIcon={<RavenIcon icon={Printer} />}
          aria-label="Print"
          onClick={() => {
            // Open the doc-style Overview print route in a new tab. It
            // re-lists the same rows from the full filter set, so the
            // report equals what's on screen. Same flow + fallback as
            // the Suggestion-detail print button.
            const url = buildOverviewPrintUrl(filters);
            // No "noopener" feature string: with it window.open always returns
            // null, so the fallback below fired too and the current tab also
            // navigated to the print route. Cut the opener by hand instead.
            const w = window.open(url, "_blank");
            if (w) {
              w.opener = null;
            } else {
              window.location.href = url;
            }
          }}
          sx={(theme) => ({
            // Matches the active sidebar nav-row text so the AppBar
            // utility actions sit in the same "selected copper" family.
            color: theme.palette.navActive ?? theme.palette.primary.dark,
            borderRadius: 999,
            minWidth: { xs: 40, sm: "auto" },
            px: { xs: 1, sm: 2 },
            "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 }, ml: 0 },
          })}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Print
          </Box>
        </Button>
        <Button
          variant="text"
          startIcon={<RavenIcon icon={Download} />}
          aria-label={exporting ? "Exporting" : "Export"}
          onClick={exportXlsx}
          disabled={allRows.length === 0 || exporting}
          sx={(theme) => ({
            // Same active-nav copper as Print — keep both AppBar
            // utility actions in the same family.
            color: theme.palette.navActive ?? theme.palette.primary.dark,
            borderRadius: 999,
            minWidth: { xs: 40, sm: "auto" },
            px: { xs: 1, sm: 2 },
            "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 }, ml: 0 },
          })}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            {exporting ? "Exporting…" : "Export"}
          </Box>
        </Button>
      </Stack>
    ),
    [exportXlsx, allRows.length, exporting, filters],
  );
  useRegisterTopBarActions(topBarActions);

  return (
    <Box>
      {/* Filter row */}
      <Box className="no-print" mb={2} data-tour="overview-scope-bar">
        <SuggestionFilters
          value={filters}
          onChange={(f) => setFilters(f)}
          departments={departments}
          categories={categories}
          defaultOpen
        />
      </Box>

      {/* Persistent scope bar — present even when no filter is active so
          the user always knows what they're looking at, and discovers the
          drill-down affordance */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 2.5, flexWrap: "wrap", rowGap: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing{" "}
          <Box
            component="span"
            sx={{ color: "text.primary", fontWeight: 600 }}
          >
            {total} {total === 1 ? "suggestion" : "suggestions"}
          </Box>
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: { sm: "auto" } }}
        >
          ↳ click any number below to open the filtered list
        </Typography>
      </Stack>

      {/* Hero strip — closure rate + meta tiles | trend chart.
        Container query: side-by-side only when the strip itself is
        wide enough that the meta-tile labels ("Median Time to Close",
        etc.) fit on one line. Below that, the trend chart drops
        beneath the closure-rate card. The 1100px threshold is the
        empirical break point where the longest tile label starts
        wrapping. */}
      <Box
        sx={{
          containerType: "inline-size",
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: "1fr",
            "@container (min-width: 1100px)": {
              gridTemplateColumns: "7fr 5fr",
            },
          }}
        >
          {/* Hero left: KPI + 3 meta tiles */}
          <Card data-tour="overview-hero">
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <HeroKPI
                  closureRate={closureRate}
                  priorRate={priorClosureRate}
                  total={total}
                  closed={closed}
                  to={urlFor({ label: "Closed", status: stateSets.closedStates })}
                  minSampleForDelta={10}
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  <MetaTile
                    label="Median Time to Close"
                    value={
                      medianCloseDays === null
                        ? "—"
                        : medianCloseDays === 0
                          ? "<1d"
                          : `${medianCloseDays}d`
                    }
                    sub={
                      closed === 0
                        ? "no closed suggestions yet"
                        : `across the ${closed} closed`
                    }
                    info={
                      <>
                        How many days it typically takes for a suggestion to
                        go from submitted to closed. We use the middle value
                        (median) so a few very slow ones don't skew the
                        number. Shows <strong>&lt;1d</strong> if most close
                        the same day, and <strong>—</strong> if nothing has
                        closed yet.
                      </>
                    }
                  />
                  <MetaTile
                    label="Aging"
                    value={String(agingCount)}
                    sub="parked > 14 days"
                    tone={agingCount > 0 ? "warn" : "neutral"}
                    to={
                      agingCount > 0
                        ? urlFor({
                            label: "Aging",
                            status: stateSets.nonTerminalStates,
                            // facet=aging tells Landing to additionally apply
                            // the client-side `updated_at < cutoff` predicate
                            // (the backend can't express that today).
                            facet: "aging",
                          })
                        : undefined
                    }
                    info={
                      <>
                        Suggestions that haven't moved forward in over 14
                        days. Only open suggestions are counted here —
                        closed and rejected ones are excluded.
                      </>
                    }
                  />
                  <MetaTile
                    label="SLA Breaches"
                    value={String(committee.slaBreaches)}
                    sub="past the 90-day window"
                    tone={committee.slaBreaches > 0 ? "warn" : "neutral"}
                    to={
                      committee.slaBreaches > 0
                        ? urlFor({
                            label: "SLA Breached",
                            // facet=sla_breached tells Landing to apply the
                            // client-side slaState(...).breached predicate
                            // (open-and-late OR closed/rejected late) — no
                            // status filter, since a breach can land on
                            // any status.
                            facet: "sla_breached",
                          })
                        : undefined
                    }
                    info={
                      <>
                        Suggestions past the 90-day SLA window from
                        submission (IEPL-CORP-2.207). Covers both open
                        suggestions still running late and closed/rejected
                        ones that missed the window before they finished.
                      </>
                    }
                  />
                  <MetaTile
                    label="Reject Rate"
                    value={total > 0 ? `${rejectRate}%` : "—"}
                    sub={total > 0 ? `${rejected} of ${total}` : "no submissions"}
                    to={
                      rejected > 0
                        ? urlFor({
                            label: "Rejected",
                            status: stateSets.rejectedStates,
                          })
                        : undefined
                    }
                    info={
                      <>
                        Percentage of suggestions that were rejected out of
                        all submissions. This number may change over time as
                        open suggestions get approved or rejected.
                      </>
                    }
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Right: trend chart */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="overline" color="text.secondary">
                    Submissions Trend
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {TREND_GRANULARITY_LABEL[trend.granularity]}
                  </Typography>
                </Stack>
                {trend.buckets.length === 0 ? (
                  <Box
                    sx={{
                      height: 140,
                      display: "grid",
                      placeItems: "center",
                      border: "1px dashed",
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      No submissions in this period
                    </Typography>
                  </Box>
                ) : (
                  <TrendChart
                    buckets={trend.buckets}
                    color={theme.palette.primary.dark}
                    granularity={trend.granularity}
                    height={140}
                  />
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Pipeline */}
      <Card sx={{ mb: 3 }} data-tour="overview-pipeline">
        <CardContent sx={{ p: 3 }}>
          <Pipeline
            stages={pipelineStages}
            total={total}
            rejections={rejectionBuckets}
          />
        </CardContent>
      </Card>

      {/* Four breakdown columns inside a single card */}
      <Card sx={{ mb: 3, overflow: "hidden" }} data-tour="overview-breakdowns">
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: 5,
            }}
          >
            <BreakdownCard
              title="By Department"
              highlight={
                topDepartment
                  ? `Highest: ${displayDepartment(topDepartment[0])} · ${
                      topDepartment[1]
                    } ${topDepartment[1] === 1 ? "suggestion" : "suggestions"}`
                  : undefined
              }
              rows={report.byDepartment.map(([k, v]) => ({
                key: displayDepartment(k),
                count: v,
                to: urlFor({
                  label: `Department · ${displayDepartment(k)}`,
                  department: [k],
                }),
              }))}
              overallTotal={total}
              defaultBarColor={theme.palette.text.secondary}
              emptyHint="No suggestions from any department in this view."
            />
            <BreakdownCard
              title="By Category"
              rows={report.byCategory.map(([k, v]) => ({
                key: k,
                count: v,
                to: urlFor({ label: `Category · ${k}`, category: [k] }),
              }))}
              overallTotal={total}
              defaultBarColor={theme.palette.text.secondary}
              emptyHint="No suggestions tagged to any category in this view."
            />
            <BreakdownCard
              title="By Status"
              rows={report.byStatus.map(({ status, label, count }) => {
                const c = statusColor(workflow, status, theme.palette.mode).dot;
                return {
                  key: label,
                  count,
                  to: urlFor({ label, status: [status] }),
                  color: c,
                  dotColor: c,
                };
              })}
              overallTotal={total}
              defaultBarColor={theme.palette.primary.main}
              emptyHint="No status data yet — submit your first suggestion."
            />
            <BreakdownCard
              title="By Reward Category"
              rows={report.byReward.map(({ key, count }) => ({
                key,
                count,
                to: urlFor({
                  label: `Reward Category · ${key}`,
                  reward_category: [key],
                }),
              }))}
              overallTotal={report.rewardTotal}
              defaultBarColor={theme.palette.warning.main}
              emptyHint="No reward categories recorded in this view."
            />
          </Box>
        </CardContent>
      </Card>

      {/* Committee report — SC/WC figures for the selected period */}
      <Card sx={{ mb: 3 }} data-tour="overview-committee">
        <CardContent sx={{ p: 3 }}>
          <AnnualSscReport
            summary={committee}
            periodLabel={inferPeriodLabel(filters.date_from, filters.date_to)}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
